import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Unstable_Grid2 as Grid,
  TextField,
  Alert,
  MenuItem,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Switch,
  AppBar,
  Toolbar,
  Typography,
  Chip,
  Paper,
} from "@mui/material";

import SelectChipField from "../../../../components/SelectChipField";
import TaskGrid from "../../../../components/TaskGrid";
import TaskHeader from "../../../../components/TaskHeader";
import StartDateDialog from "../../../../components/StartDateDialog";

import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";

import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useOrganization } from "..";
import { useAuth } from "../../../../contexts/AuthContext";
import { useDialog } from "../../../../contexts/DialogContext";

import { fetchData } from "../../../../fetchData";
import {
  organizationsRegimeTributarioLabels,
  organizationsAreasDeAtuacaoLabels,
  isSimplesNacionalRegime,
} from "../../../../utils/labels";

type FormValues = {
  isncricao_estadual: number;
  isncricao_municipal: number;
  login: string;
  password: string;
  regime: string;
  codigo_simples: string;
  area: Array<string>;
  observations: string;
};

type FiscalTask = {
  on: boolean;
  name: string;
  date: string;
  responsible: string | number;
  schedule?: {
    frequency: "daily" | "weekly" | "monthly";
    dayOfWeek: number[];
    monthsInterval?: number;
    startDate?: string;
  };
};

function scheduleStartDateToISO(
  startDate: string | undefined,
): string | undefined {
  if (!startDate) return undefined;
  if (typeof startDate !== "string") return undefined;
  if (startDate.includes("T")) return startDate;

  const parts = startDate.split("/");
  if (parts.length !== 3) return startDate;

  const [d, m, y] = parts;
  const date = new Date(Number(y), Number(m) - 1, Number(d));

  return isNaN(date.getTime()) ? startDate : date.toISOString();
}

const OrganizationFiscalTab = () => {
  const auth = useAuth();
  const snackbar = useSnackbar();
  const { data: organization, set } = useOrganization();
  const dialog = useDialog();
  const [progress, setProgress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tasks, setTasks] = useState<FiscalTask[]>([]);
  const [tasksBackup, setTasksBackup] = useState<FiscalTask[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [taskErrors, setTaskErrors] = useState<{ responsible: boolean }[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<any[]>([]);
  const [masterSwitch, setMasterSwitch] = useState(false);
  const [isObservationsFocused, setIsObservationsFocused] = useState(false);
  const [startDateDialogOpen, setStartDateDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const fieldRefs = useRef<(HTMLDivElement | null)[]>([]);
  const {
    register,
    control,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      isncricao_estadual: organization.fiscal?.isncricao_estadual || "",
      isncricao_municipal: organization.fiscal?.isncricao_municipal || "",
      login: organization.fiscal?.login || "",
      password: organization.fiscal?.password || "",
      regime: organization.fiscal?.regime || "",
      codigo_simples: organization.fiscal?.codigo_simples || "",
      area: organization.fiscal?.area || [],
      observations: organization.fiscal?.observations || "",
    },
  });

  const regime = watch("regime");

  const isApuracaoTaskDisabled = (taskName: string) => {
    if (!isEditing) return true;

    const apuracaoTasks = [
      "Apuração Fiscal Mensal - Lucro Presumido",
      "Apuração Fiscal Mensal - Lucro Real",
      "Apuração Fiscal Mensal - MEI",
      "Apuração Fiscal Mensal - Simples Nacional",
      "Apuração Fiscal Mensal - Simples Nacional Híbrido",
    ];

    if (!apuracaoTasks.includes(taskName)) {
      return false;
    }

    if (regime === "PF") {
      return true;
    }

    if (
      regime === "Lucro Presumido" &&
      taskName !== "Apuração Fiscal Mensal - Lucro Presumido"
    ) {
      return true;
    }

    if (
      regime === "Lucro Real" &&
      taskName !== "Apuração Fiscal Mensal - Lucro Real"
    ) {
      return true;
    }

    if (regime === "MEI" && taskName !== "Apuração Fiscal Mensal - MEI") {
      return true;
    }

    if (
      regime === "Simples Nacional" &&
      taskName !== "Apuração Fiscal Mensal - Simples Nacional"
    ) {
      return true;
    }

    if (
      regime === "Simples Nacional - Híbrido" &&
      taskName !== "Apuração Fiscal Mensal - Simples Nacional Híbrido"
    ) {
      return true;
    }

    return false;
  };

  const handleRegimeChange = (newValue: string) => {
    if (!isEditing) return;

    const newTasks = [...tasks];
    const apuracaoTasks = [
      "Apuração Fiscal Mensal - Lucro Presumido",
      "Apuração Fiscal Mensal - Lucro Real",
      "Apuração Fiscal Mensal - MEI",
      "Apuração Fiscal Mensal - Simples Nacional",
      "Apuração Fiscal Mensal - Simples Nacional Híbrido",
    ];

    newTasks.forEach((task) => {
      if (apuracaoTasks.includes(task.name)) {
        if (newValue === "PF") {
          task.on = false;
        } else if (
          newValue === "Lucro Presumido" &&
          task.name !== "Apuração Fiscal Mensal - Lucro Presumido"
        ) {
          task.on = false;
        } else if (
          newValue === "Lucro Real" &&
          task.name !== "Apuração Fiscal Mensal - Lucro Real"
        ) {
          task.on = false;
        } else if (
          newValue === "MEI" &&
          task.name !== "Apuração Fiscal Mensal - MEI"
        ) {
          task.on = false;
        } else if (
          newValue === "Simples Nacional" &&
          task.name !== "Apuração Fiscal Mensal - Simples Nacional"
        ) {
          task.on = false;
        } else if (
          newValue === "Simples Nacional - Híbrido" &&
          task.name !== "Apuração Fiscal Mensal - Simples Nacional Híbrido"
        ) {
          task.on = false;
        }
      }
    });

    setTasks(newTasks);
  };

  const handleTaskToggleAttempt = (taskName: string, checked: boolean) => {
    if (isApuracaoTaskDisabled(taskName)) {
      snackbar?.open(
        "Não é possível ativar esta tarefa de apuração para o regime tributário selecionado",
        "warning",
      );
      return;
    }
  };

  async function loadData() {
    setProgress(true);
    try {
      // Carrega usuários primeiro
      const usersResult = await fetchData("/runrunit/users", {
        method: "GET",
      });
      setUsers(usersResult);

      // Busca os tipos de tarefa fiscal
      const result = await fetchData(`/task-types?autoUpload=true`, {
        method: "GET",
      });
      const filtered = (result?.rows || []).filter(
        (t: any) => t.tribe && t.tribe.includes("Fiscal"),
      );
      setFilteredTasks(filtered);

      // Busca os autoTasks do cliente
      const client = await fetchData(
        `/organizations/${organization.reference}/ref`,
        { method: "GET" },
      );
      const autoTasks = client?.autoTasks || [];

      // Faz o merge: sempre exibe as tarefas do tipo fiscal, preenchendo com autoTasks se existir
      const mapped = filtered
        .filter((t: any) => {
          if (t.name && (t.name === "DCTFWeb" || t.name === "DIRF")) {
            const folhaPagamentoStatus = client.dp?.info;
            return folhaPagamentoStatus === "Não Possui";
          }
          return true;
        })
        .map((t: any) => {
          const found = autoTasks.find(
            (a: any) => a.taskTypeID === t.runrunit_id,
          );

          return {
            on: found ? found.active : false,
            name: t.name,
            date: found ? String(found.uploadDay).padStart(2, "0") : "01",
            responsible: found ? found.user : "",
            schedule: found?.schedule
              ? {
                  frequency: found.schedule.frequency || "monthly",
                  dayOfWeek: found.schedule.dayOfWeek || [],
                  monthsInterval: found.schedule.monthsInterval || 1,
                  startDate: found.schedule.startDate
                    ? new Date(found.schedule.startDate).toLocaleDateString(
                        "pt-BR",
                      )
                    : undefined,
                }
              : {
                  frequency: "monthly",
                  dayOfWeek: [],
                  monthsInterval: 1,
                },
          };
        });
      setTasks(mapped);
      setTasksBackup(mapped);

      // Reset form com dados atualizados
      reset({
        isncricao_estadual: client.fiscal?.isncricao_estadual || "",
        isncricao_municipal: client.fiscal?.isncricao_municipal || "",
        login: client.fiscal?.login || "",
        password: client.fiscal?.password || "",
        regime: client.fiscal?.regime || "",
        codigo_simples: client.fiscal?.codigo_simples || "",
        area: client.fiscal?.area || [],
        observations: client.fiscal?.observations || "",
      });
    } catch (e) {
      console.error("Erro no loadData fiscal:", e);
      setTasks([]);
      setTasksBackup([]);
      setUsers([]);
    }
    setProgress(false);
  }

  const handleMasterSwitchChange = (checked: boolean) => {
    const currentTasks = [...tasks];
    const updatedTasks = currentTasks.map((task) => {
      if (isApuracaoTaskDisabled(task.name)) {
        return { ...task, on: false };
      }
      return { ...task, on: checked };
    });

    setTasks(updatedTasks);
    setMasterSwitch(checked);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [organization.reference]);

  const onInvalid = () => {
    snackbar?.open("Preencha os campos obrigatórios.", "error");
  };

  async function onSubmit(data: any) {
    const taskValidationErrors = tasks.map((task) => ({
      responsible: task.on && !task.responsible,
    }));
    setTaskErrors(taskValidationErrors);
    const hasError = taskValidationErrors.some((err) => err.responsible);
    if (hasError) {
      // Não exibe snackbar, só deixa os campos vermelhos
      return;
    }

    // Verificar se há tarefas ativas com intervalo >= 2 meses
    const tasksWithInterval = tasks.filter(
      (task) =>
        task.on &&
        task.schedule?.frequency === "monthly" &&
        (task.schedule?.monthsInterval || 1) >= 2,
    );

    if (tasksWithInterval.length > 0) {
      setPendingFormData(data);
      setStartDateDialogOpen(true);
      return;
    }

    await saveWithConflictCheck(data);
  }

  async function saveWithConflictCheck(data: any, forceUpdate = false) {
    setSaving(true);
    // Monta o array autoTasks para salvar no backend
    // Filtra apenas as tasks que estão visíveis (não foram escondidas pelo filtro)
    const visibleTasks = tasks.filter((task) => {
      // Encontrar o taskType correspondente pelo nome
      const taskType = filteredTasks.find((ft) => ft.name === task.name);

      if (taskType?.name === "DCTFWeb" || taskType?.name === "DIRF") {
        const folhaPagamentoStatus = organization.dp?.info;
        return folhaPagamentoStatus === "Não Possui";
      }
      return true;
    });

    const autoTasks = visibleTasks.map((task) => {
      const taskType = filteredTasks.find((ft) => ft.name === task.name);

      const schedule = task.schedule
        ? {
            ...task.schedule,
            startDate: scheduleStartDateToISO(task.schedule.startDate),
          }
        : undefined;

      const autoTask = {
        taskTypeID: (taskType && taskType.runrunit_id) || null,
        taskName: (taskType && taskType.name) || "",
        active: task.on,
        uploadDay: Number(task.date) || 1,
        user: task.responsible,
        tribe: (taskType && taskType.tribe) || [],
        schedule,
      };

      return autoTask;
    });
    const payload = { ...organization, fiscal: data, autoTasks, forceUpdate };

    try {
      await fetchData(`/organizations/${organization._id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      set(payload);
      setIsEditing(false);
      setTasksBackup(tasks);
      snackbar?.open("Informações fiscais atualizadas com sucesso!", "success");
      await loadData();
    } catch (error: any) {
      // Verificar se é um erro de conflito
      const errorMessage = error?.message || "";

      if (errorMessage.startsWith("TASK_CONFLICTS:")) {
        const conflictsJson = errorMessage.replace("TASK_CONFLICTS:", "");
        try {
          const conflicts = JSON.parse(conflictsJson);

          // Usar o dialog existente
          dialog?.open(
            "Conflito de Tasks",
            <Box sx={{ p: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                As seguintes tasks não podem estar ativas ao mesmo tempo:
              </Alert>

              {conflicts.map((conflict: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: 1,
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Conflito {index + 1}:
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label="Nova Task" color="primary" size="small" />
                      <Typography variant="body2">
                        {conflict.newTask.name} (Dia{" "}
                        {conflict.newTask.uploadDay} - {conflict.newTask.user})
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label="Task Conflitante"
                        color="error"
                        size="small"
                      />
                      <Typography variant="body2">
                        {conflict.conflictingTask.name} (Dia{" "}
                        {conflict.conflictingTask.uploadDay} -{" "}
                        {conflict.conflictingTask.user})
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Alert severity="info" sx={{ mt: 2 }}>
                Se você prosseguir, as tasks conflitantes serão automaticamente
                desativadas.
              </Alert>

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button onClick={dialog?.close} color="inherit">
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    dialog?.close();
                    saveWithConflictCheck(data, true);
                  }}
                  variant="contained"
                  color="warning"
                >
                  Prosseguir e Desativar Conflitantes
                </Button>
              </Stack>
            </Box>,
          );

          return; // Não mostrar erro padrão
        } catch (e) {
          console.error("Erro ao parsear conflitos:", e);
        }
      }

      snackbar?.open(
        "Ops! No momento não foi possível realizar a ação.",
        "error",
      );
    }
    setSaving(false);
  }

  const handleStartDateDialogClose = () => {
    setStartDateDialogOpen(false);
    setPendingFormData(null);
  };

  const handleStartDateDialogConfirm = async (tasksWithStartDate: any[]) => {
    // Atualizar as tarefas com as datas de início configuradas
    const updatedTasks = tasks.map((task) => {
      const taskType = filteredTasks.find((ft) => ft.name === task.name);
      const taskTypeID = (taskType && taskType.runrunit_id) || 0;
      const updatedTask = tasksWithStartDate.find(
        (t) => t.taskTypeID === taskTypeID,
      );
      if (updatedTask && updatedTask.schedule) {
        return {
          ...task,
          schedule: updatedTask.schedule,
        };
      }
      return task;
    });

    setTasks(updatedTasks);
    setStartDateDialogOpen(false);

    // Agora salvar com os dados pendentes
    if (pendingFormData) {
      await saveWithConflictCheck(pendingFormData);
      setPendingFormData(null);
    }
  };

  // Converter tarefas para o formato esperado pelo dialog
  const tasksForDialog = tasks.map((task) => {
    const taskType = filteredTasks.find((ft) => ft.name === task.name);
    return {
      taskTypeID: (taskType && taskType.runrunit_id) || 0,
      taskName: (taskType && taskType.name) || task.name,
      active: task.on,
      uploadDay: Number(task.date),
      user: task.responsible,
      tribe: (taskType && taskType.tribe) || [],
      schedule: task.schedule,
    };
  });

  return (
    <Box sx={{ p: 2, position: "relative", pb: 4 }}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <SelectChipField
              size="small"
              name="area"
              disabled={!isEditing}
              register={register}
              get={getValues}
              required="Informe a área de atuação da organização."
              label="Área de Atuação"
              error={errors?.area ? true : false}
            >
              {organizationsAreasDeAtuacaoLabels.map((label: string) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </SelectChipField>
            {errors?.area && (
              <Alert severity="error">{errors.area.message?.toString()}</Alert>
            )}
          </Grid>
          <Grid xs={12} lg={6}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Regime Tributário"
                  size="small"
                  margin="dense"
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                  error={!!errors.regime}
                  helperText={errors.regime?.message?.toString()}
                  onChange={(e) => {
                    field.onChange(e);
                    handleRegimeChange(e.target.value);
                  }}
                >
                  {organizationsRegimeTributarioLabels.map((label: string) => (
                    <MenuItem key={label} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              control={control}
              name="regime"
              rules={{ required: "Selecione o regime tributário." }}
            />
            {errors?.regime && (
              <Alert severity="error">
                {errors.regime.message?.toString()}
              </Alert>
            )}
          </Grid>
          {isSimplesNacionalRegime(regime) && (
            <Grid xs={12} lg={6}>
              <TextField
                {...register("codigo_simples")}
                label="Código Simples Nacional"
                variant="outlined"
                fullWidth
                disabled={!isEditing}
                margin="dense"
                size="small"
              />
            </Grid>
          )}
          <Grid xs={12} lg={6}>
            <TextField
              {...register("isncricao_estadual")}
              label="Inscrição Estadual"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={6}>
            <TextField
              {...register("isncricao_municipal")}
              label="Inscrição Municipal"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={6}>
            <TextField
              {...register("login")}
              label="Login Municipal"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={6}>
            <TextField
              {...register("password")}
              label="Senha Municipal"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid xs={12}>
            <TextField
              {...register("observations")}
              label="Observações"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
              multiline
              rows={isObservationsFocused ? 10 : 5}
              onFocus={() => setIsObservationsFocused(true)}
              onBlur={() => setIsObservationsFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  (e.target as HTMLTextAreaElement).blur();
                }
              }}
              sx={{
                "& .MuiInputBase-root": {
                  transition: "all 0.3s ease-in-out",
                },
                "& .MuiInputBase-input": {
                  transition: "all 0.3s ease-in-out",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Tarefas */}
        <Divider sx={{ my: 3 }} />
        <Box>
          <TaskHeader
            activeTasksCount={tasks.filter((t) => t.on).length}
            totalTasksCount={tasks.length}
            masterSwitch={masterSwitch}
            onMasterSwitchChange={handleMasterSwitchChange}
            isEditing={isEditing}
          />
          <TaskGrid
            tasks={tasks}
            setTasks={setTasks}
            users={users}
            isEditing={isEditing}
            progress={progress}
            taskErrors={taskErrors}
            setTaskErrors={setTaskErrors}
            regimeTributario={regime}
            onTaskToggleAttempt={handleTaskToggleAttempt}
          />
        </Box>
      </form>

      {/* Botões flutuantes fixos */}
      {auth?.user.permission_level === "manager" && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            {!isEditing && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
              </Button>
            )}
            {isEditing && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={async () => {
                    setIsEditing(false);
                    setTasks(tasksBackup);
                    await loadData();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onSubmit, onInvalid)}
                >
                  {!saving && "Salvar"}
                  {saving && (
                    <CircularProgress size={12} sx={{ color: "#FFF" }} />
                  )}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      )}

      <StartDateDialog
        open={startDateDialogOpen}
        onClose={handleStartDateDialogClose}
        onConfirm={handleStartDateDialogConfirm}
        tasks={tasksForDialog}
        loading={saving}
      />
    </Box>
  );
};

export default OrganizationFiscalTab;
